/**
 * Feature access configuration for beta/early access features.
 *
 * IMPORTANT: These checks are for UX only (hiding nav links, showing
 * "coming soon" messages). They are NOT a security boundary since
 * this code is shipped in the frontend bundle.
 *
 * All feature-gated API endpoints MUST enforce authorization server-side.
 * See: src/api/core/feature_access.py for backend enforcement.
 *
 * When a feature is ready for GA:
 * 1. Remove the member UUID check from the UI components
 * 2. Update the backend to check subscription/entitlement instead
 * 3. Delete the corresponding constant from this file
 */

/**
 * Member UUIDs with access to the Creative Studio feature.
 * Using UUIDs instead of integer IDs to avoid exposing internal DB identifiers.
 *
 * NOTE: "test-uuid-0" is a test-only placeholder. Add real member UUIDs
 * (format: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx") when onboarding beta testers.
 *
 * IMPORTANT: This is duplicated from the backend (src/api/core/feature_access.py).
 * The frontend check is for UX only (hiding nav links). The backend is the
 * security boundary. Keep these in sync when adding/removing beta members.
 *
 * Creative Studio is a paid feature currently in beta testing.
 * Once billing infrastructure is ready, access will be controlled
 * by the member's subscription plan instead of this list.
 */
export const CREATIVE_STUDIO_BETA_MEMBER_UUIDS: string[] = [
  "test-uuid-0",
  "2fe97558-a557-4f45-923e-b0a22b43a16f", // Member 1 (Flouri) - beta tester
];

/**
 * Check if a member has access to the Creative Studio feature.
 */
export function hasCreativeStudioAccess(memberUuid: string | null): boolean {
  if (memberUuid === null) return false;
  return CREATIVE_STUDIO_BETA_MEMBER_UUIDS.includes(memberUuid);
}

// Meta linkage is broadly available. The old member-UUID beta allowlist
// (`META_LINKAGE_BETA_MEMBER_UUIDS` + `hasMetaLinkageAccess`) was removed
// with the free-tier single-platform flip — see
// `docs/FREE_TIER_SINGLE_PLATFORM_PLAN.md`. Entry-point visibility now
// derives from `useLinkageEligibilityQuery` (backend
// `GET /billing/linkage-eligibility`), and each Meta read hook gates on
// `hasLinkedMetaAccounts` from `useAuthStore`.

// Offline conversion upload access lives on the backend as an env-var-driven
// member_id allowlist (Settings.OFFLINE_CONVERSIONS_BETA_MEMBER_IDS). The
// frontend deliberately does NOT mirror it — see
// `useOfflineConversionsAccess` hook + `/offline-conversions/access`
// endpoint, which return whether the current user is in the allowlist.
// This avoids the sync-two-allowlists problem that Creative Studio /
// Meta linkage have today.
